import { dealerSelectors } from '@makemydeal/dr-shared-store';
import { scopedStateSelectors } from '@makemydeal/shared-scoped-store';
import type { RealState } from '@makemydeal/shared-scoped-store';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';

import * as dealTemplateSelectors from '../selectors/dealTemplateSelectors';
import * as offerReduxSelectors from '../selectors/offerRedux';
import * as vehicleSelectors from '../selectors/vehicleSelectors';

// --- Multi-Scenario Template NR Event Names ---
const NR_MULTI_SCENARIO_TEMPLATE_MANUAL_APPLY = 'MV:MultiScenarioTemplate - Manual Apply';

const NR_APPLICATION_NAME = 'MV';

type MultiScenarioTemplateExtraAttributes = { templateId?: string; templateName?: string };

/**
 * Collects the NR attributes shared by all multi-scenario template events. Returns `null` when
 * there are not enough scenarios to report on (a single-scenario deal is not "multi-scenario").
 */
const collectMultiScenarioAttributes = (state: any) => {
    const scenarioDetails = dealTemplateSelectors.getScenarioVehicleDetails(state);
    const scenarioCount = scenarioDetails.length;
    const hasMultipleScenarios = scenarioCount > 1;
    if (!hasMultipleScenarios) return null;

    const dealTypes = scenarioDetails.map((scenario) => scenario.offerType).filter(Boolean);
    const uniqueDealTypes = [...new Set(dealTypes)];
    const inventoryConditions = scenarioDetails
        .map((scenario) => {
            const scopeState = scopedStateSelectors.getScopeStateById(state as RealState, scenario.scopeId);
            return scopeState ? vehicleSelectors.getVehicleCondition(scopeState as any) : undefined;
        })
        .filter(Boolean);
    const uniqueInventoryConditions = [...new Set(inventoryConditions)];

    const vehicleYears = scenarioDetails.map((scenario) => scenario.year).filter(Boolean);
    const vehicleMakes = scenarioDetails.map((scenario) => scenario.make).filter(Boolean);
    const vehicleModels = scenarioDetails.map((scenario) => scenario.model).filter(Boolean);
    const vehicleTrims = scenarioDetails.map((scenario) => scenario.trim).filter(Boolean);

    return {
        applicationName: NR_APPLICATION_NAME,
        correlationId: offerReduxSelectors.getSessionId(state),
        dealXgId: offerReduxSelectors.getDealXgId(state),
        dealXgVersion: offerReduxSelectors.getDealXgVersion(state),
        dealerId: dealerSelectors.getDealerId(state),
        vin: vehicleSelectors.getVehicleVin(state),
        scenarioCount,
        dealTypes: dealTypes.join(','),
        uniqueDealTypes: uniqueDealTypes.join(','),
        allDealTypesSame: uniqueDealTypes.length === 1,
        inventoryTypes: uniqueInventoryConditions.join(','),
        vehicleYears: [...new Set(vehicleYears)].join(','),
        vehicleMakes: [...new Set(vehicleMakes)].join(','),
        vehicleModels: [...new Set(vehicleModels)].join(','),
        vehicleTrims: [...new Set(vehicleTrims)].join(',')
    };
};

/**
 * Logs a multi-scenario template manual apply NR event. Encapsulates attribute collection,
 * the scenario-count guard, and error handling.
 */
export const logManualApplyTemplateEvent = (state: any, extraAttributes?: MultiScenarioTemplateExtraAttributes) => {
    const attributes = collectMultiScenarioAttributes(state);
    if (!attributes) return;

    logNewRelicPageAction(NR_MULTI_SCENARIO_TEMPLATE_MANUAL_APPLY, {
        ...attributes,
        ...extraAttributes
    });
};
